// WarrantySection.jsx - Shared component for all divisions
import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateUS } from '../../utils/formatDate';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to calculate warranty status
const calculateWarrantyStatus = (startDate, years) => {
  const start = new Date(startDate);
  const endDate = new Date(start);
  endDate.setFullYear(endDate.getFullYear() + years);
  
  const now = new Date();
  const isExpired = now > endDate;
  const diffMs = endDate - now;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);
  
  return { endDate, isExpired, diffDays, diffMonths, diffYears };
};

// Helper to format date value for input
const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.includes('T')) {
    return dateStr.split('T')[0];
  }
  return dateStr;
};

// Warranty status badge component
const WarrantyStatusBadge = ({ isExpired }) => (
  <span className={`px-2 py-1 rounded text-xs font-semibold ${isExpired ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
    {isExpired ? 'EXPIRED' : 'ACTIVE'}
  </span>
);

// Time remaining display component
const TimeRemaining = ({ isExpired, diffYears, diffMonths, diffDays, endDate }) => (
  <td className={`p-2 font-mono ${isExpired ? 'text-red-600' : 'text-green-600'}`}>
    {isExpired ? (
      'Expired'
    ) : (
      <div>
        <div>{diffYears}y {diffMonths % 12}m {diffDays % 30}d</div>
        <div className="text-xs text-slate-500">
          Expires: {formatDateUS(endDate)}
        </div>
      </div>
    )}
  </td>
);

export const WarrantySection = ({
  // Division info
  divisionType, // 'door', 'window', 'curtainwall', 'storefront'
  title,
  
  // Project data
  project,
  projectId,
  warrantyStartDate,
  warrantyDateField, // e.g., 'doorWarrantyStartDate'
  
  // Types data
  types,
  typesTitle,
  getTypeLabel, // Function to get type label (e.g., type => `${type.manufacturer} - ${type.model}`)
  getWarrantyFields, // Function to get warranty fields array for a type
  
  // Glass types data
  glassTypes,
  glassTypesTitle,
  getGlassLabel, // Function to get glass label
  getGlassWarrantyYears, // Function to get warranty years from glass
  showGlassManufacturer,
  showGlassType,
  showGlassModel,
  
  // Permissions
  canEdit,
  
  // Callbacks
  onRefresh,
  
  // Test IDs
  testIdPrefix
}) => {
  // Date picker state
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Handle warranty start date change from calendar
  const handleWarrantyDateChange = async (selectedDate) => {
    if (!selectedDate) return;
    setCalendarOpen(false);
    const isoDate = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
    try {
      await axios.patch(`${API}/api/projects/${projectId}/warranty-dates`, {
        [warrantyDateField]: isoDate
      });
      toast.success('Warranty start date updated');
      onRefresh();
    } catch (error) {
      console.error('Warranty date update error:', error);
      toast.error('Failed to update date');
    }
  };

  return (
    <div data-testid={`${testIdPrefix}-warranty-section`}>
      <h2 className="text-2xl font-bold mb-6">{title}</h2>
      
      {/* Warranty Start Date Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Project Warranty Start Date</CardTitle>
        </CardHeader>
        <CardContent>
          {canEdit ? (
            <div className="flex gap-4 items-end">
              <div className="flex-1 max-w-xs">
                <Label>Warranty Start Date</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      data-testid={`${testIdPrefix}-warranty-start-date-input`}
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {warrantyStartDate ? formatDateUS(warrantyStartDate) : 'Select date...'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={warrantyStartDate ? new Date(warrantyStartDate) : undefined}
                      onSelect={handleWarrantyDateChange}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          ) : (
            <p className="text-slate-600">
              {warrantyStartDate
                ? `Warranty Start Date: ${formatDateUS(warrantyStartDate)}`
                : 'No warranty start date set'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Warranty Tables - Only show if start date is set */}
      {warrantyStartDate && (
        <div className="space-y-6">
          {/* Types Warranty Table */}
          {types && types.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{typesTitle}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid={`${testIdPrefix}-types-warranty-table`}>
                    <thead className="border-b">
                      <tr>
                        <th className="text-left p-2">Type</th>
                        <th className="text-left p-2">Warranty Type</th>
                        <th className="text-left p-2">Warranty Period</th>
                        <th className="text-left p-2">Status</th>
                        <th className="text-left p-2">Time Remaining</th>
                      </tr>
                    </thead>
                    <tbody>
                      {types.map((type) => {
                        const warranties = getWarrantyFields(type);
                        const validWarranties = warranties.filter(w => w.years);
                        
                        return warranties.map((warranty, idx) => {
                          if (!warranty.years) return null;
                          
                          const status = calculateWarrantyStatus(warrantyStartDate, warranty.years);
                          
                          return (
                            <tr key={`${type.id}-${idx}`} className="border-b">
                              {idx === 0 ? (
                                <td className="p-2 font-semibold" rowSpan={validWarranties.length}>
                                  {getTypeLabel(type)}
                                </td>
                              ) : null}
                              <td className="p-2">{warranty.label}</td>
                              <td className="p-2">{warranty.years} Years</td>
                              <td className="p-2">
                                <WarrantyStatusBadge isExpired={status.isExpired} />
                              </td>
                              <TimeRemaining {...status} />
                            </tr>
                          );
                        });
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Glass Types Warranty Table */}
          {glassTypes && glassTypes.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{glassTypesTitle}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid={`${testIdPrefix}-glass-warranty-table`}>
                    <thead className="border-b">
                      <tr>
                        <th className="text-left p-2">Type</th>
                        {showGlassManufacturer && <th className="text-left p-2">Manufacturer</th>}
                        {showGlassType && <th className="text-left p-2">Glass Type</th>}
                        {showGlassModel && <th className="text-left p-2">Model</th>}
                        <th className="text-left p-2">Warranty Period</th>
                        <th className="text-left p-2">Status</th>
                        <th className="text-left p-2">Time Remaining</th>
                      </tr>
                    </thead>
                    <tbody>
                      {glassTypes.map((glass) => {
                        const warrantyYears = getGlassWarrantyYears(glass);
                        if (!warrantyYears) return null;
                        
                        const status = calculateWarrantyStatus(warrantyStartDate, warrantyYears);
                        
                        return (
                          <tr key={glass.id} className="border-b">
                            <td className="p-2">{getGlassLabel(glass)}</td>
                            {showGlassManufacturer && (
                              <td className="p-2">{glass.glassManufacturer || glass.manufacturer || 'N/A'}</td>
                            )}
                            {showGlassType && (
                              <td className="p-2">{glass.glassType || 'N/A'}</td>
                            )}
                            {showGlassModel && (
                              <td className="p-2">{glass.model || 'N/A'}</td>
                            )}
                            <td className="p-2">{warrantyYears} Years</td>
                            <td className="p-2">
                              <WarrantyStatusBadge isExpired={status.isExpired} />
                            </td>
                            <TimeRemaining {...status} />
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default WarrantySection;
